// src/context/AuthContext.tsx
import React, { createContext, useContext, useState, ReactNode, Dispatch, SetStateAction } from 'react';
import { UserProfile } from '../models/user.model';

// Define the type for your Auth context
interface AuthContextType {
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
  user: UserProfile | null;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  error: string | null;
  showTimeoutDialog: boolean;
  setShowTimeoutDialog: (value: boolean) => void;
}

// Create the Auth context
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create the AuthProvider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showTimeoutDialog, setShowTimeoutDialog] = useState<boolean>(false);

  const login = () => {
    setIsAuthenticated(true);
    // Set user info if needed
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setError(null);
    setShowTimeoutDialog(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        user,
        loading,
        setLoading,
        error,
        showTimeoutDialog,
        setShowTimeoutDialog
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Create a hook to use the Auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }
  return context;
};
